
import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const InstallPWA: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);

    useEffect(() => {
        // Detectar si es iOS (iPhone/iPad)
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        // Verificar si ya está en modo standalone (instalada)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;

        if (isIOSDevice && !isStandalone) {
            // Mostrar instrucciones para iOS solo si no se ha cerrado antes
            const hasClosedIOSPrompt = localStorage.getItem('iosPwaPromptClosed');
            if (!hasClosedIOSPrompt) {
                // Pequeño delay para no abrumar al iniciar
                setTimeout(() => {
                    setIsIOS(true);
                    setIsVisible(true);
                }, 3000);
            }
        }

        const handleBeforeInstallPrompt = (e: Event) => {
            // Prevenir que el navegador muestre su propio prompt automáticamente
            e.preventDefault();
            // Guardar el evento para dispararlo después
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            // Mostrar nuestro botón personalizado
            setIsVisible(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Verificar si ya está instalada al cargar
        window.addEventListener('appinstalled', () => {
            setIsVisible(false);
            setDeferredPrompt(null);
            console.log('PWA instalada con éxito');
        });

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;

        // Mostrar el prompt nativo
        await deferredPrompt.prompt();

        // Esperar a que el usuario decida
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);

        // Limpiar
        setDeferredPrompt(null);
        setIsVisible(false);
    };

    const handleClose = () => {
        setIsVisible(false);
        if (isIOS) {
            localStorage.setItem('iosPwaPromptClosed', 'true');
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-4 left-4 right-4 z-[9999] sm:bottom-6 sm:left-auto sm:right-6 animate-fade-in-up">
            <div className="relative max-w-sm mx-auto sm:mx-0 group">
                {/* Glassmorphism Container with Enhanced Shadow & Gradient Border */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl backdrop-blur-xl bg-gradient-to-br from-white/95 via-white/90 to-gray-50/95 border border-white/40 p-5">
                    {/* Ambient Glow Effect */}
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Inner Content Container */}
                    <div className="relative z-10">
                        {/* Animated Background Decoration */}
                        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-32 h-32 bg-gradient-to-br from-emerald-400/30 via-teal-400/20 to-transparent rounded-full blur-2xl animate-pulse"></div>

                        {/* Close Button */}
                        <button
                            onClick={handleClose}
                            className="absolute -top-1 -right-1 text-gray-400 hover:text-gray-700 p-1.5 rounded-full hover:bg-white/60 backdrop-blur-sm transition-all hover:scale-110 active:scale-95 z-20"
                            aria-label="Cerrar"
                        >
                            <X size={18} />
                        </button>

                        <div className="flex items-start gap-4 pr-8">
                            {/* Icon with Gradient Background */}
                            <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-3xl shadow-lg transform transition-transform hover:scale-105 hover:rotate-3">
                                💇‍♀️
                            </div>

                            <div className="flex-1">
                                {/* Title with Gradient Accent */}
                                <h3 className="font-bold text-gray-900 mb-1.5 text-base flex items-center gap-2">
                                    Instalar Cresta
                                    <span className="inline-block w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full animate-pulse"></span>
                                </h3>

                                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                                    {isIOS
                                        ? "Instala nuestra App para una mejor experiencia y acceso rápido."
                                        : "Agrega Cresta a tu inicio para recibir notificaciones y acceder más rápido."
                                    }
                                </p>

                                {isIOS ? (
                                    <div className="text-xs text-gray-600 bg-gradient-to-br from-gray-50 to-white p-3.5 rounded-xl border border-gray-200/60 shadow-inner backdrop-blur-sm">
                                        <p className="mb-2 flex items-center gap-2 font-medium">
                                            <span className="text-emerald-600">1.</span> Toca el botón
                                            <span className="inline-flex items-center justify-center p-1.5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg text-blue-600 shadow-sm">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                                    <polyline points="16 6 12 2 8 6"></polyline>
                                                    <line x1="12" y1="2" x2="12" y2="15"></line>
                                                </svg>
                                            </span>
                                            Compartir
                                        </p>
                                        <p className="font-medium">
                                            <span className="text-emerald-600">2.</span> Selecciona <strong className="text-gray-800">"Agregar al inicio"</strong>
                                        </p>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleInstallClick}
                                        className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-semibold py-3 px-4 rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 group/btn"
                                    >
                                        <svg className="w-4 h-4 transition-transform group-hover/btn:translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        <span>Instalar Aplicación</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
